import React, { useEffect, useRef } from 'react';
import { FactSection } from './CollegeParts';
import VideoCard from '../videos/VideoCard';
import VideoRail from '../videos/VideoRail';
import { track, EVENTS } from '../../services/analytics';
import { SOCIAL_LINKS, socialLabel } from '../../constants/social';
import { placesOf, shownVideos } from '../../services/collegeVideos';

// "Watch: <School> on Stratia" (#4608, founder pick B of the 2026-10-17
// mocks): our own published Shorts and tutorials about this school, near the
// end of the page, before Similar schools. A lost: a strip under the stat
// tiles pushed "Where do I stand" and Ask Astra 426px down on every phone.
// The newest five; a sideways row on a phone, five across on a wide screen
// (at most SHOWN = 5, so the shared rail's arrows for a longer row, #5707,
// never draw here).
// Each card links to every place that video is live, and only those. A
// school with no video renders nothing: no heading, no empty box.
// The card is components/videos/VideoCard.jsx, shared with /videos (#4728).

export default function CollegeVideos({ college, videos }) {
    const shown = shownVideos(videos).filter((v) => placesOf(v).length > 0);
    const ref = useRef(null);
    const seen = useRef(false);
    const count = shown.length;
    useEffect(() => {
        seen.current = false;
    }, [college.slug]);
    useEffect(() => {
        const el = ref.current;
        if (!el || !count || typeof IntersectionObserver === 'undefined') return undefined;
        const io = new IntersectionObserver((entries) => {
            if (seen.current || !entries.some((e) => e.isIntersecting)) return;
            seen.current = true;
            track(EVENTS.COLLEGE_VIDEOS_SEEN, { slug: college.slug, count });
            io.disconnect();
        }, { threshold: 0.5 });
        io.observe(el);
        return () => io.disconnect();
    }, [college.slug, count]);
    if (!count) return null;
    const name = college.shortName || college.name;
    return (
        <FactSection id="watch" title={`Watch: ${name} on Stratia`}>
            <div ref={ref} data-testid="college-videos" className="space-y-3">
                <p>Short videos we made from {name}&apos;s own published numbers. Pick the place you like to watch.</p>
                <VideoRail label={college.name}>
                    {shown.map((v) => (
                        <VideoCard
                            key={v.id}
                            college={college}
                            video={v}
                            onPlay={(channel, placement) => track(EVENTS.COLLEGE_VIDEO_CLICKED, {
                                slug: college.slug, video: v.id, channel, placement,
                            })}
                        />
                    ))}
                </VideoRail>
                <p className="text-[13px] leading-snug text-[#4A4A4A]">
                    Follow Stratia Admissions on{' '}
                    {SOCIAL_LINKS.map((s, i) => (
                        <React.Fragment key={s.key}>
                            <a
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={socialLabel(s)}
                                onClick={() => track(EVENTS.COLLEGE_VIDEO_CLICKED, {
                                    slug: college.slug, video: null, channel: s.key, placement: 'follow',
                                })}
                                className="inline-flex min-h-[44px] items-center font-semibold text-[#1A4D2E] underline underline-offset-2"
                            >
                                {s.name}
                            </a>
                            {i < SOCIAL_LINKS.length - 1 ? ', ' : '.'}
                        </React.Fragment>
                    ))}
                    <span className="block text-xs text-[#6B6B6B]">Stratia is not affiliated with {name}.</span>
                </p>
            </div>
        </FactSection>
    );
}
